"use client";
import Link from "next/link";
import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import AsoebiModal from "@src/components/AsoebiModal";
import Dialog from "src/components/Dialog";

interface Props {
  eventUuid: string | undefined;
}

const AsoEbi = ({ eventUuid }: Props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "backOut",
        delay: 0.8,
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 10px 25px rgba(255, 255, 255, 0.2)",
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
      },
    },
  };

  const backgroundVariants = {
    hidden: {
      // scale: 1.1,
      opacity: 0,
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: "easeOut",
      },
    },
  };

  const [openAsoebi, setOpenAsoebi] = useState(false);

  return (
    <motion.div
      className="h-[20rem] md:h-[100vh] bg-[linear-gradient(180deg,_#00234B_0%,_#002D60_100%)] flex flex-col justify-center pt-[4rem] pb-[4rem] md:pb-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.div
        ref={ref}
        className="h-[20rem] md:h-[35rem] flex justify-center items-center relative px-8 overflow-hidden"
        style={{
          backgroundImage: `url('/images/golden-quotes.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        variants={backgroundVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        whileHover={{
          // scale: 1.02,
          transition: { duration: 0.3 },
        }}
      >
        {/* Animated overlay for depth */}
        <motion.div
          className="absolute inset-0 bg-black/20"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        />

        <motion.div
          className="flex flex-col justify-center items-center w-full lg:w-3/4 relative z-10"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h3
            className="font-ntFab font-semibold lg:font-normal text-center text-2xl mb-2 lg:text-6xl text-[#FCE2B0]"
            variants={itemVariants}
            whileHover={{
              scale: 1.02,
              textShadow: "0 0 20px rgba(255, 255, 255, 0.5)",
              transition: { duration: 0.2 },
            }}
          >
            Our Asoebi
          </motion.h3>

          <motion.h5
            className="font-ntFab font-normal text-center text-lg lg:text-2xl text-[#FCE2B0]"
            variants={itemVariants}
          >
            The beautiful attires for our wedding.
          </motion.h5>

          <Dialog
            trigger={
              <motion.div
                className="flex flex-col md:flex-row mt-8 gap-8 justify-center items-center"
                variants={itemVariants}
              >
                <motion.button
                  className="px-6 py-2 rounded transition duration-300 border border-[#FCE2B0] text-[#FCE2B0] text-xl font-ebGaramond uppercase hover:bg-[#FCE2B0] hover:text-[#00234B] relative overflow-hidden"
                  type="button"
                  variants={buttonVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  whileHover="hover"
                  whileTap="tap"
                >
                  {/* Ripple effect background */}
                  <motion.div
                    className="absolute inset-0 bg-[#FCE2B0] rounded"
                    initial={{ scale: 0, opacity: 0 }}
                    whileHover={{
                      scale: 1,
                      opacity: 0.1,
                      transition: { duration: 0.3 },
                    }}
                  />

                  {/* Animated border glow */}
                  <motion.div
                    className="absolute inset-0 border border-[#FCE2B0] rounded"
                    animate={{
                      boxShadow: [
                        "0 0 0px rgba(255, 255, 255, 0.5)",
                        "0 0 10px rgba(255, 255, 255, 0.8)",
                        "0 0 0px rgba(255, 255, 255, 0.5)",
                      ],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                  />

                  <p className="relative z-10">Get Your Asoebi</p>
                </motion.button>
              </motion.div>
            }
            open={openAsoebi}
            onOpenChange={setOpenAsoebi}
          >
            <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />
          </Dialog>
        </motion.div>

        {/* Floating decorative elements */}
        <motion.div
          className="absolute top-10 left-10 w-2 h-2 bg-[#FCE2B0]/30 rounded-full"
          animate={{
            y: [0, -10, 0],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
        />

        <motion.div
          className="absolute bottom-20 right-20 w-3 h-3 bg-[#FCE2B0]/20 rounded-full"
          animate={{
            y: [0, -15, 0],
            x: [0, 5, 0],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: 4,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
            delay: 1,
          }}
        />
      </motion.div>
    </motion.div>
  );
};

export default AsoEbi;
